import { useEffect, useRef, useState } from "react";
import { X, Building2 } from "lucide-react";
import { useCreateOrg } from "../../hooks/useAPI";
import { toast } from "sonner";

// Defense-in-depth: string-redact errors so closure refs back to request
// payloads can't leak into devtools. Matches the helper used across the
// other converged pages.
function redactErrorForDev(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (typeof err === 'string') return err;
  return String(err);
}

// Standard focusable selector for the focus trap (WAI-ARIA APG).
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

interface CreateOrgModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const INDUSTRIES = [
  { value: "technology", label: "Technology" },
  { value: "finance", label: "Finance & Banking" },
  { value: "healthcare", label: "Healthcare" },
  { value: "legal", label: "Legal & Compliance" },
  { value: "insurance", label: "Insurance" },
  { value: "retail", label: "Retail & E-Commerce" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "government", label: "Government & Public Sector" },
  { value: "education", label: "Education" },
  { value: "other", label: "Other" },
];

const REGIONS = [
  { value: "eu", label: "European Union" },
  { value: "uk", label: "United Kingdom" },
  { value: "us", label: "United States" },
  { value: "apac", label: "Asia-Pacific" },
  { value: "other", label: "Other" },
];

export function CreateOrgModal({ isOpen, onClose }: CreateOrgModalProps) {
  const createOrg = useCreateOrg();

  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("technology");
  const [region, setRegion] = useState("eu");
  const [billingEmail, setBillingEmail] = useState("");
  const [errors, setErrors] = useState<{ name?: string; billingEmail?: string }>({});

  // WAI-ARIA dialog refs — capture invoker on open so close returns focus
  // instead of dumping keyboard users on <body>.
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // ESC + Tab focus trap + body scroll lock + focus restoration — full
  // WAI-ARIA dialog pattern (cat 11/22 a11y + cross-component consistency,
  // WCAG 2.1.1 / 2.4.3).
  useEffect(() => {
    if (!isOpen) {
      const prev = previousFocusRef.current;
      if (prev && document.contains(prev)) prev.focus();
      return;
    }
    previousFocusRef.current = (document.activeElement as HTMLElement) ?? null;
    // Move focus into dialog AFTER prev-capture. Was previously via input
    // `autoFocus` — but autoFocus fires before useEffect, so prev captured
    // the input itself and restore-on-close became a silent no-op on a
    // detached node. RAF defers until DOM paint.
    const focusRaf = requestAnimationFrame(() => {
      const firstField = dialogRef.current?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled])'
      );
      firstField?.focus();
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !createOrg.isPending) {
        e.preventDefault();
        handleClose();
        return;
      }
      if (e.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) { e.preventDefault(); return; }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      cancelAnimationFrame(focusRaf);
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, createOrg.isPending]);

  if (!isOpen) return null;

  const validate = () => {
    const e: typeof errors = {};
    if (!name.trim()) e.name = "Organization name is required";
    else if (name.trim().length > 100) e.name = "Must be 100 characters or fewer";
    if (billingEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingEmail)) {
      e.billingEmail = "Please enter a valid email address";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    createOrg.mutate(
      { name: name.trim(), industry, region, billingEmail: billingEmail.trim() || undefined },
      {
        onSuccess: () => {
          toast.success("Organization created", { description: name.trim() });
          window.location.href = "/dashboard";
        },
        onError: (err: unknown) => {
          if (import.meta.env.DEV) {
            console.warn("[CreateOrgModal] create org failed:", redactErrorForDev(err));
          }
          const e = err as { message?: string; code?: string } | undefined;
          if (e?.message?.includes("already exists") || e?.code === "NAME_TAKEN") {
            setErrors({ name: "An organization with this name already exists" });
          } else {
            // Cardinal Rule #4: don't echo raw err.message — backend
            // error strings can leak operational detail. Static fallback
            // covers every non-name-taken failure mode.
            toast.error("Failed to create organization. Please try again.");
          }
        },
      }
    );
  };

  const handleClose = () => {
    if (createOrg.isPending) return;
    setName("");
    setIndustry("technology");
    setRegion("eu");
    setBillingEmail("");
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} aria-hidden="true" />

      {/* Modal */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-org-title"
        className="relative adp-modal z-10 w-[90vw] max-w-[600px]"
      >
        {/* Header */}
        <div className="flex items-start justify-between pb-4 border-b border-border">
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2 border border-border bg-surface-1">
              <Building2 className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 id="create-org-title" className="text-lg font-semibold text-foreground">Create Organization</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Add a new organization to your account</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={createOrg.isPending}
            aria-label="Close"
            className="p-1 hover:bg-surface-2 border border-transparent hover:border-border transition-colors flex-shrink-0 disabled:opacity-40 focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--foreground)] focus-visible:outline-offset-0"
          >
            <X className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          </button>
        </div>

        {/* Fields */}
        <div className="space-y-4 pt-4">
          {/* Name */}
          <div>
            <label htmlFor="create-org-name" className="block text-[10px] text-mono uppercase tracking-wider text-muted-foreground mb-1.5">
              Organization Name <span className="text-foreground">*</span>
            </label>
            <input
              id="create-org-name"
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors(p => ({ ...p, name: undefined })); }}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              placeholder="e.g., Acme AG"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "create-org-name-err" : undefined}
              className="w-full px-3 py-2 bg-background border border-border text-foreground text-sm focus:outline-none focus:border-brand placeholder:text-muted-foreground/40 transition-colors"
              style={errors.name ? { borderColor: "var(--foreground)" } : {}}
            />
            {errors.name && (
              <p id="create-org-name-err" className="mt-1 text-[11px] text-muted-foreground">{errors.name}</p>
            )}
          </div>

          {/* Industry */}
          <div>
            <label htmlFor="create-org-industry" className="block text-[10px] text-mono uppercase tracking-wider text-muted-foreground mb-1.5">
              Industry
            </label>
            <select
              id="create-org-industry"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border text-foreground text-sm focus:outline-none focus:border-brand transition-colors appearance-none"
            >
              {INDUSTRIES.map((i) => (
                <option key={i.value} value={i.value}>{i.label}</option>
              ))}
            </select>
          </div>

          {/* Region */}
          <div>
            <label htmlFor="create-org-region" className="block text-[10px] text-mono uppercase tracking-wider text-muted-foreground mb-1.5">
              Primary Region
            </label>
            <select
              id="create-org-region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border text-foreground text-sm focus:outline-none focus:border-brand transition-colors appearance-none"
            >
              {REGIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {/* Billing Email */}
          <div>
            <label htmlFor="create-org-email" className="block text-[10px] text-mono uppercase tracking-wider text-muted-foreground mb-1.5">
              Billing Email <span className="text-muted-foreground/50">(optional)</span>
            </label>
            <input
              id="create-org-email"
              type="email"
              value={billingEmail}
              onChange={(e) => { setBillingEmail(e.target.value); setErrors(p => ({ ...p, billingEmail: undefined })); }}
              placeholder="billing@company.com"
              aria-invalid={!!errors.billingEmail}
              aria-describedby={errors.billingEmail ? "create-org-email-err" : undefined}
              className="w-full px-3 py-2 bg-background border border-border text-foreground text-sm focus:outline-none focus:border-brand placeholder:text-muted-foreground/40 transition-colors"
              style={errors.billingEmail ? { borderColor: "var(--foreground)" } : {}}
            />
            {errors.billingEmail && (
              <p id="create-org-email-err" className="mt-1 text-[11px] text-muted-foreground">{errors.billingEmail}</p>
            )}
          </div>
        </div>

        <div className="border-t border-border mt-6 mb-5" />

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={handleClose}
            disabled={createOrg.isPending}
            className="adp-btn flex-1 text-[11px] text-mono uppercase tracking-wider py-2 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={createOrg.isPending || !name.trim()}
            className="adp-btn-primary flex-1 text-[11px] text-mono uppercase tracking-wider py-2 disabled:opacity-50"
          >
            {createOrg.isPending ? "Creating..." : "Create Organization"}
          </button>
        </div>
      </div>
    </div>
  );
}
